import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material';
import { ModalCloseComponent } from '@app/shared';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { BrandingType, BrandingTypeScale } from '../models/branding.interfaces';
import { BrandingApiService } from '../services/branding-api.service';

@Component({
  selector: 'app-branding',
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrandingComponent implements OnInit, OnDestroy {
  @Input() enableEdit: boolean;

  @Input('selectedTabIndex')
  set selectedTabIndex(index) {
    this.tabIndex = index;
    this.getBrandingTypes();
    this.getScales();
  }

  @Input('serviceId')
  set setService(serviceId: number) {
    this.serviceId = serviceId;
    this.scales = undefined;
    this.getScales();
  }

  brandingForm: FormGroup;
  scalesForm: FormGroup;

  selectedBrandingIndex = 0;

  brandingTypeName$: BehaviorSubject<string> = new BehaviorSubject('');
  brandingTypeId$: BehaviorSubject<string> = new BehaviorSubject('');

  private serviceId: number;
  private tabIndex: number;
  private brandingTypes: BrandingType[];
  private scales: BrandingTypeScale[];

  private dialogRef: MatDialogRef<ModalCloseComponent>;
  private componentDestroyed$ = new Subject();

  constructor(
    private brandingApiService: BrandingApiService,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  get brandingTypesFormArray(): FormArray {
    if (this.brandingForm) {
      return this.brandingForm.controls.types as FormArray;
    }
  }

  get scalesFormArray(): FormArray {
    if (this.scalesForm) {
      return this.scalesForm.controls.scales as FormArray;
    }
  }

  ngOnInit(): void {
    this.brandingApiService.brandingTypes$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter((types: any[]) => types.length > 0)
      )
      .subscribe(types => {
        this.selectedBrandingIndex = 0;
        this.brandingTypeName$.next(types[0].name);
        this.brandingTypeId$.next(types[0].id);
        this.brandingTypes = types;
        this.buildTypesForm(types);
      });
    this.brandingApiService.brandingTypeScales$
      .pipe(takeUntil(this.componentDestroyed$), debounceTime(300))
      .subscribe(scales => {
        if (scales && scales.length > 0) {
          this.scales = scales;
        }
        this.buildScalesForm(scales);
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.unsubscribe();
  }

  addBrandingType(): void {
    this.brandingTypesFormArray.push(this.buildTypeFormGroup());
  }

  selectBrandingType(index: number): void {
    this.selectedBrandingIndex = index;
    this.brandingTypeName$.next(this.brandingTypesFormArray.value[index].name);
    this.brandingTypeId$.next(this.brandingTypesFormArray.value[index].id);
  }

  saveBrandingType(index: number): void {
    const brandingTypeValue = this.brandingTypesFormArray.controls[index].value;
    if (brandingTypeValue.id) {
      if (this.brandingTypes[index].name !== brandingTypeValue.name) {
        this.brandingApiService.putBrandingType(brandingTypeValue.name, brandingTypeValue.id);
      } else {
        (this.brandingTypesFormArray.controls[index] as FormGroup).controls.mode.patchValue('show');
      }
    } else {
      this.brandingApiService.postBrandingType(brandingTypeValue.name);
    }
  }

  deleteBrandingType(index: number): void {
    const brandingTypeValue = this.brandingTypesFormArray.controls[index].value;
    if (brandingTypeValue.id) {
      const config = new MatDialogConfig();
      config.data = {
        headerTemplate: `UTAX_FRONT_DELETE_BRANDING_TYPE`
      };
      this.dialogRef = this.dialog.open(ModalCloseComponent, config);
      this.dialogRef
        .afterClosed()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(res => {
          if (res === 'true') {
            this.brandingApiService.deleteBrandingType(brandingTypeValue.id);
          }
        });
    } else {
      this.brandingTypesFormArray.removeAt(index);
    }
  }

  editBrandingType(index: number): void {
    (this.brandingTypesFormArray.controls[index] as FormGroup).controls.mode.patchValue('edit');
  }

  saveScale(index: number): void {
    const scaleValue = (this.scalesFormArray.controls[index] as FormGroup).getRawValue();
    const brandingTypeId = this.brandingTypesFormArray.controls[this.selectedBrandingIndex].value.id;
    if (scaleValue.id) {
      this.brandingApiService.putBrandingScale({
        id: scaleValue.id,
        branding_type_id: brandingTypeId,
        requests_quantity: scaleValue.requests_quantity,
        value: scaleValue.value,
        is_enabled: scaleValue.is_enabled ? scaleValue.is_enabled : false
      });
    } else {
      this.brandingApiService.postBrandingScale({
        requests_quantity: scaleValue.requests_quantity,
        value: scaleValue.value,
        branding_type_id: brandingTypeId,
        service_id: this.serviceId,
        is_enabled: scaleValue.is_enabled ? scaleValue.is_enabled : false
      });
    }
  }

  saveAllScales(): void {
    this.scalesFormArray.controls.forEach((group: FormGroup, index: number) => {
      if (group.touched) {
        this.saveScale(index);
      }
    });
  }

  deleteScale(index: number): void {
    const scaleValue = this.scalesFormArray.controls[index].value;
    if (scaleValue.id) {
      const config = new MatDialogConfig();
      config.data = {
        headerTemplate: `UTAX_FRONT_DELETE_BRANDING_TYPE_SCALE`
      };
      this.dialogRef = this.dialog.open(ModalCloseComponent, config);
      this.dialogRef
        .afterClosed()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(res => {
          if (res === 'true') {
            this.brandingApiService.deleteScale(scaleValue.id);
          }
        });
    } else {
      this.scalesFormArray.removeAt(index);
    }
  }

  addScale(): void {
    this.scalesFormArray.push(
      this.buildScaleFormGroup({
        branding_type: { id: this.brandingTypesFormArray.controls[this.selectedBrandingIndex].value.id }
      })
    );
  }

  private getBrandingTypes(): void {
    if (this.tabIndex === 7 && !this.brandingTypes) {
      this.brandingApiService.getBrandingTypes();
    }
  }

  private getScales(): void {
    if (this.tabIndex === 7 && this.serviceId && !this.scales) {
      this.brandingApiService.getBrandingScales(`${this.serviceId}`);
    }
  }

  // form building
  private buildTypeFormGroup(type?: BrandingType): FormGroup {
    return this.fb.group({
      name: [type && type.name ? type.name : '', [Validators.required]],
      id: [type && type.id ? type.id : ''],
      mode: [type ? 'show' : 'edit'] // edit | show
    });
  }

  private buildTypesForm(types: BrandingType[]): void {
    this.brandingForm = this.fb.group({
      types: this.fb.array([])
    });
    types.forEach(type => {
      this.brandingTypesFormArray.push(this.buildTypeFormGroup(type));
    });
    this.cd.detectChanges();
  }

  private buildScaleFormGroup(scale?: any): FormGroup {
    return this.fb.group({
      id: [scale && scale.id ? scale.id : ''],
      branding_type_id: [scale && scale.branding_type ? scale.branding_type.id : ''],
      is_enabled: [scale ? scale.is_enabled : false],
      requests_quantity: [
        scale && scale.requests_quantity ? scale.requests_quantity : 0,
        [Validators.required, Validators.min(0)]
      ],
      value: [scale && scale.value ? scale.value : 0, [Validators.required, Validators.min(0)]]
    });
  }

  private buildScalesForm(scales: BrandingTypeScale[]): void {
    this.scalesForm = this.fb.group({
      scales: this.fb.array([])
    });
    scales.forEach(scale => {
      this.scalesFormArray.push(this.buildScaleFormGroup(scale));
    });
    this.cd.detectChanges();
  }
}



// WEBPACK FOOTER //
// ./src/app/branding/branding/branding.component.ts